/**
 * G0 (Get Found tactical wire) — the pure "what does this screen honestly
 * show" decisions extracted out of Profiles.tsx's JSX so they are
 * unit-testable without a DOM harness, per this lane's own test law (house
 * pure-logic idiom, no DOM harness). Every function here answers ONE
 * NO-INVENTION question: given the served data (or its absence), what is the
 * truthful thing to render? None of these functions ever synthesize a value
 * that wasn't actually served — an absent signal always resolves to an
 * explicit "nothing to show" sentinel, never a fabricated placeholder
 * (mirrors noFabricatedPlaceholders.guard.test.ts's law for this same file).
 */

/** The score card's headline number. `null`/`undefined` score (unvalidated
 *  platform criteria, or no saved profile at all) renders the honest em-dash
 *  glyph — never a fabricated 0, which would read as "you scored zero,"
 *  a real (bad) claim about the user rather than "not yet scored." */
export function scoreDisplay(score: number | null | undefined): string {
  return score === null || score === undefined ? '—' : String(score);
}

/** Whether the recommendedTitles chip row should render at all. An empty
 *  served array is a real, honest "the platform lake had nothing to
 *  recommend yet" — this hides the row entirely rather than rendering an
 *  empty card shell or a placeholder chip. */
export function shouldShowRecommendedTitles(titles: string[] | null | undefined): boolean {
  return Array.isArray(titles) && titles.length > 0;
}

/** Whether the declared-target-roles fallback row should render — only when
 *  there are no recommended titles to show instead (mirrors Profiles.tsx's
 *  existing else-if chain: recommendedTitles takes priority when both are
 *  present, so the screen never shows two overlapping "roles" chip rows at
 *  once). */
export function shouldShowDeclaredRolesFallback(
  titles: string[] | null | undefined,
  targetRoles: string[],
): boolean {
  return !shouldShowRecommendedTitles(titles) && targetRoles.length > 0;
}

/** The Get Found score card's note line — the ONE piece of copy that must
 *  never overclaim. `hasEverGenerated` distinguishes "never generated at
 *  all" (point at the Generate button) from "generated, but this platform's
 *  criteria are still being validated" (a real, different honest state) from
 *  "scored" (the number is real). Mirrors RealProfiles' existing ternary in
 *  Profiles.tsx, extracted here so it is independently testable. */
export function scoreCardNote(input: {
  hasEverGenerated: boolean;
  score: number | null | undefined;
  platformLabel: string | null;
}): string {
  if (!input.hasEverGenerated) {
    return "You haven't generated a profile yet — do that from the button below.";
  }
  if (input.score !== null && input.score !== undefined) {
    return `Scored for ${input.platformLabel ?? 'your platform'}, from your latest generate.`;
  }
  return `Criteria for ${input.platformLabel ?? 'your platform'} are still being validated — no score to show yet.`;
}

/**
 * A role edit was just saved. The honest, non-overclaiming confirmation —
 * this MUST NOT imply the Jobs tab updates instantly (the flywheel re-derive
 * is G1, locked, not yet wired — see get-found-plan-2026-07-05.md's Break B1).
 * Renders once per successful save, then clears (the caller owns the timing).
 */
export const ROLE_SAVED_NOTE = "Saved — she'll use these next time she looks for roles like this.";

/**
 * GAP 3 (Get Found paste pack) — the paste-shape resolver every CopyButton in
 * Profiles.tsx calls: prefer the server's platform-SHAPED paste string
 * (renderPresencePaste — the correct separator/bullet-glyph/section-inclusion
 * for whichever platform is selected) and fall back to the raw, unshaped
 * field only when `paste` is absent entirely (an older saved row predating
 * this field, or a served response from before this widening — see
 * PresenceProfileResult's own doc comment in api.ts). Extracted to its own
 * pure function (rather than left as an inline `paste?.field ?? raw` at every
 * JSX call site) so the fallback rule has its own test coverage independent
 * of a component render — this codebase's own house pure-logic idiom (no DOM
 * harness needed to prove a value-in/value-out decision).
 *
 * `rawFallback` is ALWAYS a real, already-served value (never invented here)
 * — this function only ever picks between two genuine sources, it never
 * synthesizes a third.
 */
export function resolvePasteText(
  pasteField: string | null | undefined,
  rawFallback: string,
): string {
  return pasteField ?? rawFallback;
}
